import React, { useRef, useState, useCallback } from 'react';
import {
  User,
  MapPin,
  Mail,
  Phone,
  Camera,
  Upload,
  X,
  MoreVertical,
  Edit,
  Trash2,
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { useToast } from '../../../hooks/use-toast';

export default function ProfileHeader({
  name,
  role,
  organization,
  location,
  email,
  phone,
  profilePicture,
  bannerPhoto,
  isReadOnly = false,
}) {
  const { showToast } = useToast();
  const [localProfile, setLocalProfile] = useState(profilePicture);
  const [localBanner, setLocalBanner] = useState(bannerPhoto);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [showBannerOptions, setShowBannerOptions] = useState(false);
  const [showProfileOptions, setShowProfileOptions] = useState(false);

  const bannerInputRef = useRef(null);
  const profileInputRef = useRef(null);

  const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_FILE_SIZE_MB = 5;

  const validateImage = file => {
    if (!file) return false;
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      showToast('Please upload a JPEG, PNG, or WEBP image.', 'error');
      return false;
    }
    if (file.size / (1024 * 1024) > MAX_FILE_SIZE_MB) {
      showToast(`Image is too large. Max size is ${MAX_FILE_SIZE_MB}MB.`, 'error');
      return false;
    }
    return true;
  };

  const resetInputValue = input => {
    if (input && input.current) {
      input.current.value = '';
    }
  };

  const uploadToFirebase = async (file, path) => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('No authenticated user found. Please log in.');
    }

    const storage = getStorage();
    const storageRef = ref(storage, `${path}/${user.uid}/${Date.now()}_${file.name}`);

    await uploadBytes(storageRef, file);

    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
  };

  const updateFirestoreDocument = async (field, url) => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('No authenticated user found. Please log in.');
    }

    const db = getFirestore();
    const userDocRef = doc(db, 'clients', user.uid);

    await updateDoc(userDocRef, {
      [field]: url,
      updatedAt: new Date(),
    });
  };

  const removeImage = async field => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('No authenticated user found. Please log in.');
    }

    const db = getFirestore();
    const userDocRef = doc(db, 'clients', user.uid);

    await updateDoc(userDocRef, {
      [field]: null,
      updatedAt: new Date(),
    });
  };

  const handleBannerUpload = useCallback(async file => {
    if (!validateImage(file)) return;
    setUploadingBanner(true);

    try {
      const downloadURL = await uploadToFirebase(file, 'banners');

      await updateFirestoreDocument('bannerPhoto', downloadURL);

      setLocalBanner(downloadURL);
      showToast('Banner updated successfully!', 'success');
    } catch (e) {
      showToast(e.message || 'Failed to upload banner.', 'error');
    } finally {
      setUploadingBanner(false);
      resetInputValue(bannerInputRef);
    }
  }, []);

  const handleProfileUpload = useCallback(async file => {
    if (!validateImage(file)) return;
    setUploadingProfile(true);

    try {
      const downloadURL = await uploadToFirebase(file, 'profiles');

      await updateFirestoreDocument('profilePicture', downloadURL);

      setLocalProfile(downloadURL);
      showToast('Profile photo updated successfully!', 'success');
    } catch (e) {
      showToast(e.message || 'Failed to upload profile photo.', 'error');
    } finally {
      setUploadingProfile(false);
      resetInputValue(profileInputRef);
    }
  }, []);

  const handleBannerRemove = useCallback(async () => {
    try {
      await removeImage('bannerPhoto');
      setLocalBanner(null);
      showToast('Banner photo removed successfully!', 'success');
    } catch (e) {
      showToast(e.message || 'Failed to remove banner photo.', 'error');
    }
  }, []);

  const handleProfileRemove = useCallback(async () => {
    try {
      await removeImage('profilePicture');
      setLocalProfile(null);
      showToast('Profile photo removed successfully!', 'success');
    } catch (e) {
      showToast(e.message || 'Failed to remove profile photo.', 'error');
    }
  }, []);

  const handleBannerModify = useCallback(() => {
    bannerInputRef.current?.click();
    setShowBannerOptions(false);
  }, []);

  const handleProfileModify = useCallback(() => {
    profileInputRef.current?.click();
    setShowProfileOptions(false);
  }, []);

  const handleBannerDelete = useCallback(async () => {
    setShowBannerOptions(false);
    await handleBannerRemove();
  }, [handleBannerRemove]);

  const handleProfileDelete = useCallback(async () => {
    setShowProfileOptions(false);
    await handleProfileRemove();
  }, [handleProfileRemove]);

  const onDrop = e => {
    if (isReadOnly) return;
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleBannerUpload(file);
  };

  const onDragOver = e => {
    if (!isReadOnly) e.preventDefault();
  };

  const bannerSrc = localBanner || bannerPhoto;
  const profileSrc = localProfile || profilePicture;

  React.useEffect(() => {
    setLocalProfile(profilePicture);
  }, [profilePicture]);

  React.useEffect(() => {
    setLocalBanner(bannerPhoto);
  }, [bannerPhoto]);

  React.useEffect(() => {
    const handleClickOutside = event => {
      if (!event.target.closest('.options-menu')) {
        setShowBannerOptions(false);
        setShowProfileOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative overflow-hidden">
      <div
        className="relative h-64 w-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900"
        onDrop={onDrop}
        onDragOver={onDragOver}
        aria-label="Profile banner"
      >
        {bannerSrc ? (
          <>
            <img
              src={bannerSrc}
              alt="Banner"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
          </>
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center text-slate-300">
              <svg
                className="mx-auto mb-2 h-8 w-8 opacity-80"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
                <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="2" />
                <polyline points="21 15 16 10 5 21" strokeWidth="2" />
              </svg>
              <p className="text-sm opacity-90">
                {isReadOnly ? 'No banner' : 'Drag & drop a banner or click Upload'}
              </p>
            </div>
          </div>
        )}

        {!isReadOnly && (
          <div className="absolute right-4 top-4">
            <div className="options-menu relative">
              <button
                onClick={() => setShowBannerOptions(!showBannerOptions)}
                className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-2 text-sm font-medium text-white ring-1 ring-white/20 backdrop-blur-md transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/60"
                title="Banner options"
              >
                {uploadingBanner ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <MoreVertical className="h-4 w-4" />
                  </>
                )}
              </button>

              {showBannerOptions && !uploadingBanner && (
                <div className="absolute right-0 top-full mt-2 w-48 rounded-lg bg-white/95 py-2 shadow-xl ring-1 ring-black/10 backdrop-blur-md">
                  <button
                    onClick={handleBannerModify}
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Edit className="h-4 w-4" />
                    {bannerSrc ? 'Change Banner' : 'Upload Banner'}
                  </button>
                  {bannerSrc && (
                    <button
                      onClick={handleBannerDelete}
                      className="flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove Banner
                    </button>
                  )}
                </div>
              )}

              <input
                ref={bannerInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                disabled={uploadingBanner}
                onChange={e => e.target.files?.[0] && handleBannerUpload(e.target.files[0])}
              />
            </div>
          </div>
        )}
      </div>

      <div className="relative mx-auto -mt-16 max-w-5xl px-4 pb-10 sm:px-6">
        <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-6 shadow-xl backdrop-blur-xl">
          <div className="flex flex-col items-center gap-6 text-center md:flex-row md:items-end md:gap-8 md:text-left">
            <div className="relative">
              <div className="h-36 w-36 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 p-1 shadow-2xl ring-2 ring-white/20">
                {profileSrc ? (
                  <img
                    src={profileSrc}
                    alt="Profile"
                    className="h-full w-full rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center rounded-full bg-white">
                    <User className="h-16 w-16 text-slate-600" />
                  </div>
                )}
              </div>

              {!isReadOnly && (
                <div className="absolute -bottom-2 -right-2">
                  <div className="options-menu relative">
                    <button
                      onClick={() => setShowProfileOptions(!showProfileOptions)}
                      className="grid h-11 w-11 place-items-center rounded-full border-4 border-slate-900 bg-slate-700 text-white shadow-lg transition hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-white/60"
                      title="Profile photo options"
                      aria-label="Profile photo options"
                    >
                      {uploadingProfile ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      ) : (
                        <MoreVertical className="h-5 w-5" />
                      )}
                    </button>

                    {showProfileOptions && !uploadingProfile && (
                      <div className="absolute right-0 top-0 z-50 mt-2 w-48 rounded-lg bg-white/95 py-2 shadow-xl ring-1 ring-black/10 backdrop-blur-md">
                        <button
                          onClick={handleProfileModify}
                          className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Edit className="h-4 w-4" />
                          {profileSrc ? 'Change Photo' : 'Upload Photo'}
                        </button>
                        {profileSrc && (
                          <button
                            onClick={handleProfileDelete}
                            className="flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                            Remove Photo
                          </button>
                        )}
                      </div>
                    )}

                    <input
                      ref={profileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      disabled={uploadingProfile}
                      onChange={e => e.target.files?.[0] && handleProfileUpload(e.target.files[0])}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="space-y-1">
                {organization && (
                  <h1 className="text-3xl font-bold tracking-tight text-white md:text-4xl">
                    {organization}
                  </h1>
                )}
                {role && <p className="text-lg text-slate-200">{role}</p>}
                {name && (
                  <p className="text-sm text-slate-300/90">
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="hidden h-4 w-4 sm:inline" />
                      <span className="font-medium text-slate-200">{name}</span>
                    </span>
                  </p>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm text-slate-300 md:justify-start">
                {location && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/10 backdrop-blur-sm">
                    <MapPin className="h-4 w-4" />
                    {location}
                  </span>
                )}
                {email && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/10 backdrop-blur-sm">
                    <Mail className="h-4 w-4" />
                    {email}
                  </span>
                )}
                {phone && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/10 backdrop-blur-sm">
                    <Phone className="h-4 w-4" />
                    {phone}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
